import { calculateTotalAmount } from "../../../shared/utils/areaCalculation";
import { getCircleColor } from "../legacy";
import { normalizeSeedUnit } from "../services/seedAssignments";
import { getSeedProductColor } from "./seedProductColors";
import { type SeedProduct, type StripeAssignments } from "./seedFlow";

const FIELD_STANDARD_OWNER = "field-standard";
const FIELD_STANDARD_COLOR = "#b99af3";

/**
 * Return the first nonblank color.
 *
 * Empty strings must be normalized before using ?? because API hydration
 * represents missing colors as "".
 */
export const getNonEmptySeedColor = (
  ...colors: Array<string | null | undefined>
): string | undefined => {
  for (const color of colors) {
    if (color !== undefined && color !== null && color.trim() !== "") {
      return color.trim();
    }
  }

  return undefined;
};

export const calculateStripSeedQuantity = (
  rate: number,
  areaM2: number,
  unit: string
): number => {
  if (!Number.isFinite(rate) || rate < 0) {
    throw new Error("Every selected strip must have a valid rate.");
  }

  if (!Number.isFinite(areaM2) || areaM2 <= 0) {
    throw new Error("Every selected strip must have a valid saved area.");
  }

  const normalizedUnit = normalizeSeedUnit(unit);

  if (!/^(seeds|kg)\/(m2|ha)$/.test(normalizedUnit)) {
    throw new Error(`Unsupported seed measure unit: ${unit}.`);
  }

  // The legacy calculator expects hectares, including for /m2 rates.
  const rawQuantity = calculateTotalAmount(
    rate,
    areaM2 / 10000,
    normalizedUnit
  );

  // Match the legacy Seeds flow: round seed counts, not kg.
  const quantity = normalizedUnit.startsWith("seeds/")
    ? Math.round(rawQuantity)
    : rawQuantity;

  if (!Number.isFinite(quantity) || quantity < 0) {
    throw new Error("A calculated seed quantity is invalid.");
  }

  return quantity;
};

export const getStripSeedReplicantMultiplier = (
  replicants: unknown
): number => {
  if (Array.isArray(replicants)) {
    return 1 + replicants.length;
  }

  if (replicants !== null && typeof replicants === "object") {
    return 1 + Object.keys(replicants).length;
  }

  return 1;
};

/**
 * Resolve the colors used in persisted strip assignment payloads.
 *
 * Keep this existing behavior separate from client-side display colors so a
 * visual shade adjustment cannot change saved API color values.
 */
export const getStripSeedColors = (
  assignments: StripeAssignments,
  products: SeedProduct[],
  unit: string
): Record<string, string> => {
  const result: Record<string, string> = {};

  // Preserve product ordering because the legacy helper uses it to
  // choose the variety's palette.
  const trialProducts = products
    .filter((product) => product.id !== FIELD_STANDARD_OWNER)
    .map((product) => ({
      variety: product.name,
    }));

  const productsById = new Map(
    products.map((product) => [product.id, product])
  );

  Object.entries(assignments).forEach(([stripeId, assignment]) => {
    const product = productsById.get(assignment.productId);

    if (assignment.productId === FIELD_STANDARD_OWNER) {
      result[stripeId] =
        getNonEmptySeedColor(assignment.color, product?.color) ??
        FIELD_STANDARD_COLOR;
      return;
    }

    if (assignment.legacy === true) {
      result[stripeId] =
        getNonEmptySeedColor(assignment.color, product?.color) ?? "";
      return;
    }

    const rate = assignment.rate;

    if (
      product === undefined ||
      typeof rate !== "number" ||
      !Number.isFinite(rate) ||
      rate <= 0
    ) {
      result[stripeId] =
        getNonEmptySeedColor(assignment.color, product?.color) ?? "";
      return;
    }

    // Group by product ID, avoiding collisions between products that
    // happen to have the same variety label.
    const siblingRates = Object.values(assignments)
      .filter(
        (item) =>
          item.productId === assignment.productId &&
          typeof item.rate === "number" &&
          Number.isFinite(item.rate) &&
          item.rate > 0
      )
      .map((item) => ({
        variety: product.name,
        rate: item.rate,
      }));

    const calculatedColor = getCircleColor(
      product.name,
      rate,
      trialProducts,
      normalizeSeedUnit(unit),
      siblingRates
    );

    result[stripeId] =
      getNonEmptySeedColor(calculatedColor, product.color, assignment.color) ??
      "";
  });

  return result;
};

/**
 * Match the old Rates & Dosage strip color calculation for display. The old
 * strip map shades a product against its actual rates across the assigned
 * strips; configured options/ranges and other products do not affect it.
 * Standard and legacy assignments keep their existing presentation colors.
 */
export const getStripSeedDisplayColors = (
  assignments: StripeAssignments,
  products: SeedProduct[]
): Record<string, string> => {
  const result: Record<string, string> = {};
  const seedProducts = products
    .filter((product) => product.id !== FIELD_STANDARD_OWNER)
    .map((product) => ({
      id: product.id,
      name: product.name,
      options: product.densityOptions,
      range: { min: product.min, max: product.max },
    }));
  const productsById = new Map(
    products.map((product) => [product.id, product])
  );

  Object.entries(assignments).forEach(([stripeId, assignment]) => {
    const product = productsById.get(assignment.productId);

    if (assignment.productId === FIELD_STANDARD_OWNER) {
      result[stripeId] =
        getNonEmptySeedColor(assignment.color, product?.color) ??
        FIELD_STANDARD_COLOR;
      return;
    }

    if (assignment.legacy === true) {
      result[stripeId] =
        getNonEmptySeedColor(assignment.color, product?.color) ?? "";
      return;
    }

    const seedProduct = seedProducts.find(
      (item) => item.id === assignment.productId
    );

    if (
      product === undefined ||
      seedProduct === undefined ||
      typeof assignment.rate !== "number" ||
      !Number.isFinite(assignment.rate) ||
      assignment.rate <= 0
    ) {
      result[stripeId] =
        getNonEmptySeedColor(assignment.color, product?.color) ?? "";
      return;
    }

    const sameProductRates = Object.values(assignments)
      .filter(
        (item) =>
          item.productId === assignment.productId &&
          typeof item.rate === "number" &&
          Number.isFinite(item.rate) &&
          item.rate > 0
      )
      .map((item) => item.rate);

    result[stripeId] =
      getSeedProductColor(
        seedProduct,
        seedProducts,
        assignment.rate,
        sameProductRates
      ) ??
      getNonEmptySeedColor(product.color, assignment.color) ??
      "";
  });

  return result;
};
